import { useCallback, useMemo, useState } from 'react';

import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { applySeriesVisibility, getSeriesId } from '@/components/Chart/utils/seriesVisibility';

const useSeriesVisibility = (series: ExtendedSeriesOptionsType[] | undefined) => {
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const availableSeriesIds = useMemo(() => {
    if (!series?.length) return new Set<string>();
    return new Set(series.map(item => getSeriesId(item)).filter((id): id is string => Boolean(id)));
  }, [series]);

  const filteredHiddenSeriesIds = useMemo(
    () => hiddenSeriesIds.filter(id => availableSeriesIds.has(id)),
    [availableSeriesIds, hiddenSeriesIds]
  );

  const toggleSeriesVisibility = useCallback((id: string) => {
    setHiddenSeriesIds(prev => {
      if (prev.includes(id)) return prev.filter(item => item !== id);
      return [...prev, id];
    });
  }, []);

  const applyVisibility = useCallback(
    (items: ExtendedSeriesOptionsType[] | undefined) => {
      return applySeriesVisibility(items, filteredHiddenSeriesIds);
    },
    [filteredHiddenSeriesIds]
  );

  const visibleSeriesIds = useMemo(() => {
    if (!series?.length) return [];
    return series
      .map(item => getSeriesId(item))
      .filter((id): id is string => Boolean(id))
      .filter(id => !filteredHiddenSeriesIds.includes(id));
  }, [filteredHiddenSeriesIds, series]);

  return {
    hiddenSeriesIds: filteredHiddenSeriesIds,
    setHiddenSeriesIds,
    toggleSeriesVisibility,
    applyVisibility,
    visibleSeriesIds
  };
};

export default useSeriesVisibility;
